import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api.js';
import { t, getal, wanneer } from '../i18n.js';
import { St, NessusErnst } from '../components/Status.jsx';

/**
 * Eén gescand adres (docs/besluiten/nessus-koppeling.md): de open poorten met per poort de kwetsbaarheden, wat
 * sinds de vorige scan veranderde, en de bevindingen over het adres zelf (poort 0). Info staat ingeklapt.
 */
export default function ScanAdres() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['scan-adres', id], queryFn: () => api.scanAdres(id) });
  const [info, zetInfo] = useState(false);
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) {
    return <><Link className="crumb" to="/scans">← {t('Scans')}</Link>
      <p>{q.error.status === 404 ? t('Dit adres bestaat niet') : t(q.error.message)}</p></>;
  }
  const a = q.data.data;
  const telt = (f) => info || f.severity !== 'info';
  const aantalInfo = [...a.ports.flatMap((p) => p.findings), ...a.hostFindings].filter((f) => f.severity === 'info').length;

  return (
    <>
      <Link className="crumb" to="/scans">← {t('Scans')}</Link>
      <div className="page-head"><div>
        <h1 className="mono">{a.ip}</h1>
        <p className="sub">{[a.hostname, a.tenant.name, a.os].filter(Boolean).join(' · ')}</p>
        <p className="muted" style={{ fontSize: 13 }}>{t('Gescand {wanneer} in {scan}', { wanneer: a.scannedAt ? wanneer(a.scannedAt) : '—', scan: a.scan })}</p>
      </div><div><NessusErnst e={a.maxSeverity} /></div></div>

      {a.changes && (a.changes.closedPorts.length > 0 || a.changes.fixed.length > 0 || a.ports.some((p) => p.new) || a.ports.some((p) => p.findings.some((f) => f.new && f.severity !== 'info'))) && (
        <section className="card" style={{ marginBottom: 18 }}>
          <header><h2>{t('Sinds de vorige scan')}</h2><span className="meta">{a.changes.since ? wanneer(a.changes.since) : ''}</span></header>
          <ul className="steps" style={{ listStyle: 'none', paddingLeft: 0 }}>
            {a.ports.filter((p) => p.new).map((p) => <li key={`n${p.port}${p.protocol}`}><St soort={p.expected ? 'neutral' : 'warning'}>{t('open')}</St> <span className="mono">{p.port}/{p.protocol}</span> {p.service ?? ''}{p.expected && <span className="muted"> · {t('verwacht')}</span>}</li>)}
            {a.changes.closedPorts.map((p) => <li key={`d${p.port}${p.protocol}`}><St soort="good">{t('dicht')}</St> <span className="mono">{p.port}/{p.protocol}</span> {p.service ?? ''}</li>)}
            {a.ports.flatMap((p) => p.findings.filter((f) => f.new && f.severity !== 'info').map((f) => (
              <li key={`f${f.id}`}><NessusErnst e={f.severity} /> {t('nieuw')}: {f.name} <span className="mono muted">{p.port}/{p.protocol}</span></li>)))}
            {a.changes.fixed.map((f, i) => <li key={`o${i}`}><St soort="good">{t('opgelost')}</St> {f.name} {f.port && <span className="mono muted">{f.port}/{f.protocol}</span>}</li>)}
          </ul>
        </section>
      )}

      <div className="filters">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={info} onChange={(e) => zetInfo(e.target.checked)} />
          {t('Ook informatieve bevindingen ({n})', { n: getal(aantalInfo) })}</label>
      </div>

      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Open poorten')}</h2><span className="meta">{getal(a.ports.length)}</span></header>
        {a.ports.length ? a.ports.map((p) => (
          <div key={`${p.port}/${p.protocol}`} className="poort">
            <div className="poort-kop">
              <b className="mono">{p.port}/{p.protocol}</b> <span>{p.service ?? '—'}</span>
              {p.tls && <span className="pill">TLS</span>}{p.new && <St soort={p.expected ? 'neutral' : 'warning'}>{t('nieuw sinds de vorige scan')}</St>}
              {p.expected && <St soort="good">{t('verwacht')}</St>}
              <span className="muted" style={{ marginLeft: 'auto' }}>{((n) => (n === 1 ? t('1 kwetsbaarheid') : n ? t('{n} kwetsbaarheden', { n }) : t('geen kwetsbaarheden')))(
                p.findings.filter((f) => f.severity !== 'info').length)}</span>
            </div>
            {p.expected && <p className="soft" style={{ margin: '4px 0 6px', fontSize: 13 }}>{p.expected}</p>}
            {p.findings.filter(telt).map((f) => <Bevinding key={f.id} f={f} />)}
          </div>
        )) : <p className="empty">{t('Geen open poorten gevonden.')}</p>}
      </section>

      {a.hostFindings.filter(telt).length > 0 && (
        <section className="card" style={{ marginBottom: 18 }}>
          <header><h2>{t('Over het adres zelf')}</h2><span className="meta">{t('niet aan een poort gebonden')}</span></header>
          {a.hostFindings.filter(telt).map((f) => <Bevinding key={f.id} f={f} />)}
        </section>
      )}

      {a.history.length > 1 && (
        <section className="card">
          <header><h2>{t('Eerdere scans')}</h2></header>
          <table>
            <thead><tr><th>{t('Gescand')}</th><th>{t('Scan')}</th><th>{t('Open poorten')}</th><th>{t('Kwetsbaarheden')}</th><th /></tr></thead>
            <tbody>{a.history.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.scannedAt ? wanneer(r.scannedAt) : '—'}</td><td>{r.scan}</td>
                <td className="mono">{getal(r.openPorts)}</td><td className="mono">{getal(r.findings)}</td>
                <td>{r.id === a.id ? <span className="muted">{t('deze')}</span> : <Link to={`/scans/${r.id}`}>{t('bekijken')}</Link>}</td>
              </tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </>
  );
}

function Bevinding({ f }) {
  const [open, zetOpen] = useState(false);
  return (
    <div className="bevinding">
      <button type="button" className="bevinding-kop" onClick={() => zetOpen(!open)} aria-expanded={open}>
        <NessusErnst e={f.severity} />
        <span>{f.name}{f.new && <> <span className="pill">{t('nieuw')}</span></>}</span>
        <span className="mono muted">{f.cvss3 ?? f.cvss2 ?? ''}{f.exploit && <> <St soort="critical">{t('exploit')}</St></>}</span>
      </button>
      {open && (
        <div className="bevinding-tekst">
          {f.synopsis && <p><b>{f.synopsis}</b></p>}
          {f.description && <p className="pre">{f.description}</p>}
          {f.solution && <><h4>{t('Oplossing')}</h4><p className="pre">{f.solution}</p></>}
          {f.output && <><h4>{t('Wat Nessus zag')}</h4><pre className="mono">{f.output}</pre></>}
          <p className="muted" style={{ fontSize: 12.5 }}>
            {t('Plugin')} <span className="mono">{f.pluginId}</span>{f.family ? ` · ${f.family}` : ''}
            {f.cvss3 !== null && <> · CVSS v3 <span className="mono">{f.cvss3}</span></>}{f.cvss2 !== null && <> · CVSS v2 <span className="mono">{f.cvss2}</span></>}
            {f.cves.length > 0 && <> · {f.cves.map((c, i) => <span key={c}>{i ? ', ' : ''}<a href={`https://nvd.nist.gov/vuln/detail/${c}`} target="_blank" rel="noreferrer" className="mono">{c}</a></span>)}</>}
          </p>
          {f.seeAlso.length > 0 && <p style={{ fontSize: 12.5 }}>{f.seeAlso.slice(0, 5).map((u) => <span key={u}><a href={u} target="_blank" rel="noreferrer">{u}</a><br /></span>)}</p>}
        </div>
      )}
    </div>
  );
}
